import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Button,
  Chip,
  Alert as MuiAlert,
  TablePagination,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  People,
  Visibility,
  AccessTime,
  TrendingUp,
  Refresh,
  CardMembership,
  LocalOffer,
  ToggleOn,
  ManageAccounts,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../design-system/components';
import colors from '../design-system/colors';
import shadows from '../design-system/shadows';
import { getUsageData, getUsersAnalytics } from '../services/analyticsService';
import { getAdminUsers } from '../services/adminUsersService';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';

const ANALYTICS_CONFIGURATION_MESSAGE = 'Los datos de analytics requieren Application Insights configurado.';

const getSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getPageName = (value) => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || 'Sin nombre';
};

const roundMetric = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const transformUsageResponse = (payload) => {
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const pageMap = new Map();

  results.forEach((item) => {
    const pageName = getPageName(item?.name);
    const existing = pageMap.get(pageName) || {
      path: pageName,
      name: pageName,
      totalVisits: 0,
      userIds: new Set(),
      userDisplayNames: new Set(),
      avgDurationTotal: 0,
      entries: 0,
    };

    existing.totalVisits += getSafeNumber(item?.viewCount);
    if (item?.user_AuthenticatedId) {
      existing.userIds.add(item.user_AuthenticatedId);
      existing.userDisplayNames.add(item?.userName || item.user_AuthenticatedId);
    }
    existing.avgDurationTotal += getSafeNumber(item?.avgDuration);
    existing.entries += 1;

    pageMap.set(pageName, existing);
  });

  const pageStats = Array.from(pageMap.values())
    .map((page) => ({
      path: page.path,
      name: page.name,
      totalVisits: page.totalVisits,
      uniqueUsers: page.userIds.size,
      userDisplayNames: Array.from(page.userDisplayNames),
      avgTime: page.entries > 0 ? roundMetric(page.avgDurationTotal / page.entries) : 0,
    }))
    .sort((a, b) => b.totalVisits - a.totalVisits);

  return {
    activeUsers: new Set(results.map((item) => item?.user_AuthenticatedId).filter(Boolean)).size,
    activeUserDisplayNames: Array.from(
      new Set(results.map((item) => item?.userName || item?.user_AuthenticatedId).filter(Boolean))
    ),
    totalPageViews: results.reduce((sum, item) => sum + getSafeNumber(item?.viewCount), 0),
    topPages: pageStats.map((page) => page.path),
    pageStats,
  };
};

const transformUsersResponse = (payload, profiles) => {
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const profilesById = new Map(
    (Array.isArray(profiles) ? profiles : []).map((profile) => [
      profile?.id,
      profile,
    ])
  );

  return results.map((row, index) => {
    const userId = row?.user_AuthenticatedId;
    const profile = profilesById.get(userId);
    return {
      id: userId || `analytics-user-${index}`,
      nombre: typeof profile?.full_name === 'string' ? profile.full_name.trim() : '',
      email: typeof profile?.email === 'string' ? profile.email.trim() : '',
      pagesVisited: getSafeNumber(row?.uniquePages),
      totalTime: getSafeNumber(row?.totalTimeMinutes),
      lastActivity: row?.lastActive || null,
      topPages: [],
    };
  });
};

const getAnalyticsErrorMessage = (error) => {
  if (error?.status === 500) {
    return ANALYTICS_CONFIGURATION_MESSAGE;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Error al cargar los datos de analytics';
};

const AdminAnalyticsPageModern = () => {
  usePageTitle('Consola Admin');
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [searchTerm, setSearchTerm] = useState('');
  const [usageData, setUsageData] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [orderBy, setOrderBy] = useState('totalTime');
  const [orderDir, setOrderDir] = useState('desc');
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [pagesPage, setPagesPage] = useState(0);
  const [pagesRowsPerPage, setPagesRowsPerPage] = useState(10);

  const TIME_RANGES = [
    { value: '1d', label: t('timeRange.1d') },
    { value: '7d', label: t('timeRange.7d') },
    { value: '30d', label: t('timeRange.30d') },
  ];

  useEffect(() => {
    if (user?.rol === 'admin') {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
      setLoading(false);
    }
  }, [user?.rol]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, timeRange]);

  useEffect(() => {
    setUsersPage(0);
    setPagesPage(0);
  }, [timeRange, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usage, users, profiles] = await Promise.all([
        getUsageData(timeRange, null, navigate),
        getUsersAnalytics(timeRange, navigate),
        getAdminUsers(),
      ]);
      setUsageData(transformUsageResponse(usage));
      setUsersData(transformUsersResponse(users, profiles));
    } catch (err) {
      setError(getAnalyticsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Sorting
  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDir('desc');
    }
  };

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    let list = [...usersData];
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter((u) => [u.nombre, u.email].some((value) => value.toLowerCase().includes(term)));
    }
    list.sort((a, b) => {
      const aVal = a[orderBy] ?? 0;
      const bVal = b[orderBy] ?? 0;
      if (typeof aVal === 'string') {
        return orderDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return orderDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return list;
  }, [usersData, searchTerm, orderBy, orderDir]);

  // Derived summary stats
  const summary = useMemo(() => {
    if (!usageData && usersData.length === 0) {
      return { activeUsers: 0, totalPageViews: 0, avgSessionTime: '0m', topPages: [] };
    }
    const activeUsers = usageData?.activeUsers ?? usersData.length;
    const totalPageViews = usageData?.totalPageViews ?? usersData.reduce((sum, u) => sum + (u.pagesVisited || 0), 0);
    const totalMinutes = usersData.reduce((sum, u) => sum + (u.totalTime || 0), 0);
    const avgMin = usersData.length > 0 ? Math.round(totalMinutes / usersData.length) : 0;
    const topPages = usageData?.topPages || [];
    return { activeUsers, totalPageViews, avgSessionTime: `${avgMin}m`, topPages };
  }, [usageData, usersData]);

  // Pages table data
  const pagesData = useMemo(() => {
    return usageData?.pageStats || summary.topPages || [];
  }, [usageData, summary.topPages]);

  const paginatedUsers = useMemo(() => {
    const start = usersPage * usersRowsPerPage;
    return filteredUsers.slice(start, start + usersRowsPerPage);
  }, [filteredUsers, usersPage, usersRowsPerPage]);

  const paginatedPages = useMemo(() => {
    const start = pagesPage * pagesRowsPerPage;
    return pagesData.slice(start, start + pagesRowsPerPage);
  }, [pagesData, pagesPage, pagesRowsPerPage]);

  const handleUsersPageChange = (_event, newPage) => {
    setUsersPage(newPage);
  };

  const handleUsersRowsPerPageChange = (event) => {
    setUsersRowsPerPage(parseInt(event.target.value, 10));
    setUsersPage(0);
  };

  const handlePagesPageChange = (_event, newPage) => {
    setPagesPage(newPage);
  };

  const handlePagesRowsPerPageChange = (event) => {
    setPagesRowsPerPage(parseInt(event.target.value, 10));
    setPagesPage(0);
  };

  // Access denied view
  if (!isAdmin && !loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 480, borderRadius: 3, boxShadow: shadows.md }}>
          <Typography variant="h5" fontWeight={700} color={colors.error.main} gutterBottom>
            {t('accessDenied.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('accessDenied.description')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{
              bgcolor: colors.primary[600],
              '&:hover': { bgcolor: colors.primary[700] },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('accessDenied.back')}
          </Button>
        </Paper>
      </Box>
    );
  }

  const summaryCards = [
    {
      label: t('summary.activeUsers'),
      value: summary.activeUsers,
      icon: <People />,
      color: colors.primary[600],
      bgColor: colors.primary[50],
    },
    {
      label: t('summary.totalPageViews'),
      value: summary.totalPageViews,
      icon: <Visibility />,
      color: colors.info[700],
      bgColor: colors.info[50],
    },
    {
      label: t('summary.avgSessionTime'),
      value: summary.avgSessionTime,
      icon: <AccessTime />,
      color: colors.warning[700],
      bgColor: colors.warning[50],
    },
    {
      label: t('summary.topPages'),
      value: summary.topPages.length > 0 ? summary.topPages.length : '—',
      icon: <TrendingUp />,
      color: colors.success[700],
      bgColor: colors.success[50],
    },
  ];

  const userColumns = [
    { id: 'nombre', label: t('users.table.name') },
    { id: 'email', label: t('users.table.email') },
    { id: 'pagesVisited', label: t('users.table.pagesVisited'), numeric: true },
    { id: 'totalTime', label: t('users.table.totalTime'), numeric: true },
    { id: 'lastActivity', label: t('users.table.lastActivity') },
    { id: 'topPages', label: t('pages.title'), sortable: false },
  ];

  return (
    <Box className="wp-page" sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Error banner */}
      {error && (
        <MuiAlert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchData}>
              Reintentar
            </Button>
          }
        >
          {error}
        </MuiAlert>
      )}

      {/* Sticky header */}
      <Box
        className="wp-heading"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              aria-label={t('accessDenied.back')}
              sx={{ color: colors.primary[600] }}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography component="h1" variant="h4" fontWeight={700} color="text.primary">
                {t('pageTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('summary.activeUsers')}
              </Typography>
            </Box>
            <IconButton onClick={fetchData} aria-label={t('refresh')} sx={{ color: colors.primary[600] }}>
              <Refresh />
            </IconButton>
          </Box>

          {/* Time range filter */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {TIME_RANGES.map((tr) => (
              <Chip
                key={tr.value}
                label={tr.label}
                onClick={() => setTimeRange(tr.value)}
                sx={{
                  fontWeight: 600,
                  bgcolor: timeRange === tr.value ? 'primary.main' : 'action.hover',
                  color: timeRange === tr.value ? 'primary.contrastText' : 'text.primary',
                  '&:hover': {
                    bgcolor: timeRange === tr.value ? 'primary.dark' : 'action.selected',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box className="wp-body" sx={{ p: { xs: 2, md: 3 } }}>

        {/* Quick access admin sections */}
        <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
          Gestión
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { label: 'Usuarios y roles', icon: <ManageAccounts />, path: '/admin/usuarios', color: colors.primary[600], bg: colors.primary[50] },
            { label: 'Licencias', icon: <CardMembership />, path: '/admin/licencias', color: colors.success[700], bg: colors.success[50] },
            { label: 'Códigos Promocionales', icon: <LocalOffer />, path: '/admin/promo-codes', color: colors.warning[700], bg: colors.warning[50] },
            { label: 'Feature Flags', icon: <ToggleOn />, path: '/admin/feature-flags', color: colors.info[700], bg: colors.info[50] },
          ].map((item) => (
            <Grid item xs={6} md={3} key={item.path}>
              <Paper
                component="button"
                type="button"
                onClick={() => navigate(item.path)}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  font: 'inherit',
                  border: 1,
                  borderColor: 'divider',
                  p: 2.5,
                  borderRadius: 3,
                  boxShadow: shadows.sm,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.1s',
                  '&:hover': { boxShadow: shadows.md, transform: 'translateY(-2px)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                  {item.icon}
                </Box>
                <Typography fontWeight={600} color="text.primary" variant="body2">
                  {item.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Summary cards */}
        {loading ? (
          <Skeleton type="stats" stats={4} />
        ) : (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((card) => (
              <Grid item xs={6} md={3} key={card.label}>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <Paper
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      boxShadow: shadows.sm,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.label}
                      </Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Users table */}
        <Paper sx={{ borderRadius: 3, boxShadow: shadows.sm, mb: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              borderBottom: 1, borderColor: 'divider',
            }}
          >
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('users.title')}
            </Typography>
            <TextField
              size="small"
              placeholder={t('users.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
          </Box>

          {loading ? (
            <Skeleton type="table" rows={5} columns={5} />
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm ? t('users.noSearchResult') : t('users.noData')}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small" aria-label={t('users.title')}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      {userColumns.map((col) => (
                        <TableCell
                          key={col.id}
                          align={col.numeric ? 'right' : 'left'}
                          sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
                        >
                          {col.sortable === false ? (
                            col.label
                          ) : (
                            <TableSortLabel
                              active={orderBy === col.id}
                              direction={orderBy === col.id ? orderDir : 'asc'}
                              onClick={() => handleSort(col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user, idx) => (
                      <TableRow
                        key={user.id || idx}
                        hover
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600} color="text.primary">
                              {user.nombre || t('users.noName')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere', minWidth: 180 }}>
                          {user.email || t('users.noEmail')}
                        </TableCell>
                        <TableCell align="right">{user.pagesVisited ?? 0}</TableCell>
                        <TableCell align="right">{user.totalTime ?? 0}</TableCell>
                        <TableCell>
                          {user.lastActivity
                            ? new Date(user.lastActivity).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(user.topPages || []).slice(0, 3).map((page, i) => (
                              <Chip
                                key={i}
                                label={page}
                                size="small"
                                sx={{
                                  bgcolor: colors.primary[50],
                                  color: colors.primary[700],
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                }}
                              />
                            ))}
                            {(!user.topPages || user.topPages.length === 0) && (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={usersPage}
                onPageChange={handleUsersPageChange}
                rowsPerPage={usersRowsPerPage}
                onRowsPerPageChange={handleUsersRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
              />
            </>
          )}
        </Paper>

        {/* Pages table */}
        <Paper sx={{ borderRadius: 3, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: 1, borderColor: 'divider',
            }}
          >
          <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('pages.title')}
            </Typography>
          </Box>

          {loading ? (
            <Skeleton type="table" rows={5} columns={4} />
          ) : pagesData.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No hay datos de páginas disponibles
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {t('pages.table.page')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {t('pages.table.visits')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {t('pages.table.uniqueUsers')}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {t('pages.table.avgTime')}
              </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPages.map((page, idx) => (
                      <TableRow key={page.path || idx} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} color="text.primary">
                            {page.path || page.name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{page.totalVisits ?? 0}</TableCell>
                        <TableCell align="right">{page.uniqueUsers ?? 0}</TableCell>
                        <TableCell align="right">{page.avgTime ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={pagesData.length}
                page={pagesPage}
                onPageChange={handlePagesPageChange}
                rowsPerPage={pagesRowsPerPage}
                onRowsPerPageChange={handlePagesRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminAnalyticsPageModern;
